import { RUN_DURATION, TOTAL_POWER } from "../core/config";
import { systemLabel, translation } from "../core/i18n";
import type { GameSnapshot, Language, SystemId } from "../core/types";

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element #${id}`);
  return element as T;
}

function formatTime(seconds: number): string {
  const rounded = Math.max(0, Math.ceil(seconds));
  return `${String(Math.floor(rounded / 60)).padStart(2, "0")}:${String(rounded % 60).padStart(2, "0")}`;
}

export interface HudCallbacks {
  onStart: () => void;
  onPause: () => void;
  onResume: () => void;
  onRestart: () => void;
  onRoutePower: (system: SystemId) => void;
  onSonar: () => void;
  onAudioToggle: () => boolean;
}

export class HudController {
  private language: Language =
    new URLSearchParams(window.location.search).get("lang") === "ko" ? "ko" : "en";
  private toastTimer = 0;

  private readonly startScreen = byId<HTMLElement>("start-screen");
  private readonly pauseScreen = byId<HTMLElement>("pause-screen");
  private readonly resultScreen = byId<HTMLElement>("result-screen");
  private readonly hullMeter = byId<HTMLElement>("hull-meter");
  private readonly shieldMeter = byId<HTMLElement>("shield-meter");
  private readonly hullValue = byId<HTMLOutputElement>("hull-value");
  private readonly shieldValue = byId<HTMLOutputElement>("shield-value");
  private readonly waveLabel = byId<HTMLElement>("wave-label");
  private readonly timerLabel = byId<HTMLTimeElement>("timer-label");
  private readonly scoreLabel = byId<HTMLElement>("score-label");
  private readonly missionText = byId<HTMLElement>("mission-text");
  private readonly bossRail = byId<HTMLElement>("boss-rail");
  private readonly bossMeter = byId<HTMLElement>("boss-meter");
  private readonly bossValue = byId<HTMLOutputElement>("boss-value");
  private readonly onboardingCue = byId<HTMLElement>("onboarding-cue");
  private readonly comboDisplay = byId<HTMLElement>("combo-display");
  private readonly comboValue = byId<HTMLElement>("combo-value");
  private readonly surgeRail = byId<HTMLElement>("surge-rail");
  private readonly surgeMeter = byId<HTMLElement>("surge-meter");
  private readonly surgeValue = byId<HTMLOutputElement>("surge-value");
  private readonly sonarButton = byId<HTMLButtonElement>("sonar-button");
  private readonly toast = byId<HTMLElement>("toast");
  private readonly systemButtons = Array.from(
    document.querySelectorAll<HTMLButtonElement>("[data-system]"),
  );

  constructor(callbacks: HudCallbacks) {
    byId<HTMLButtonElement>("start-button").addEventListener("click", callbacks.onStart);
    byId<HTMLButtonElement>("pause-button").addEventListener("click", callbacks.onPause);
    byId<HTMLButtonElement>("resume-button").addEventListener("click", callbacks.onResume);
    byId<HTMLButtonElement>("restart-from-pause").addEventListener("click", callbacks.onRestart);
    byId<HTMLButtonElement>("restart-button").addEventListener("click", callbacks.onRestart);
    this.sonarButton.addEventListener("click", callbacks.onSonar);
    byId<HTMLButtonElement>("audio-button").addEventListener("click", () => {
      const enabled = callbacks.onAudioToggle();
      this.updateAudio(enabled);
    });
    byId<HTMLButtonElement>("share-button").addEventListener("click", () => void this.share());

    for (const button of this.systemButtons) {
      button.addEventListener("click", () => {
        const system = button.dataset.system as SystemId;
        callbacks.onRoutePower(system);
      });
    }

    for (const button of document.querySelectorAll<HTMLButtonElement>("[data-language]")) {
      button.addEventListener("click", () => {
        this.language = button.dataset.language as Language;
        this.applyLanguage();
      });
    }
    this.applyLanguage();
  }

  update(snapshot: GameSnapshot): void {
    document.body.dataset.phase = snapshot.phase;
    this.startScreen.hidden = snapshot.phase !== "menu";
    this.pauseScreen.hidden = snapshot.phase !== "paused";
    this.resultScreen.hidden = snapshot.phase !== "won" && snapshot.phase !== "lost";

    const text = translation(this.language);
    const hull = Math.max(0, snapshot.player.hull);
    const shieldPercent =
      snapshot.player.maxShield > 0
        ? (snapshot.player.shield / snapshot.player.maxShield) * 100
        : 0;
    this.hullMeter.style.transform = `scaleX(${hull / 100})`;
    this.shieldMeter.style.transform = `scaleX(${shieldPercent / 100})`;
    this.hullValue.value = String(Math.ceil(hull));
    this.shieldValue.value = String(Math.ceil(shieldPercent));
    this.hullMeter.parentElement?.setAttribute("aria-valuenow", String(Math.ceil(hull)));
    this.shieldMeter.parentElement?.setAttribute(
      "aria-valuenow",
      String(Math.ceil(shieldPercent)),
    );

    this.waveLabel.textContent = `${text.wave} ${snapshot.wave}`;
    this.timerLabel.textContent = formatTime(snapshot.remainingTime);
    this.timerLabel.dateTime = `PT${Math.ceil(snapshot.remainingTime)}S`;
    this.scoreLabel.textContent = String(snapshot.score).padStart(6, "0");
    this.missionText.textContent = text[`mission${snapshot.wave}` as keyof typeof text] as string;
    const onboardingStep = !snapshot.onboarding.moved
      ? "move"
      : !snapshot.onboarding.routed
        ? "power"
        : !snapshot.onboarding.pulsed
          ? "sonar"
          : "done";
    document.body.dataset.onboarding = onboardingStep;
    this.onboardingCue.hidden = onboardingStep === "done" || snapshot.phase !== "playing";
    this.onboardingCue.setAttribute(
      "aria-label",
      onboardingStep === "move"
        ? text.onboardingMove
        : onboardingStep === "power"
          ? text.onboardingPower
          : text.onboardingSonar,
    );

    this.comboDisplay.hidden = snapshot.player.combo < 2 || snapshot.phase !== "playing";
    this.comboValue.textContent = `x${snapshot.player.combo}`;
    this.comboDisplay.dataset.tier = String(Math.min(4, Math.floor(snapshot.player.combo / 3)));

    const surge = snapshot.player.overdriveFor;
    this.surgeRail.hidden = surge <= 0 || snapshot.phase !== "playing";
    this.surgeMeter.style.transform = `scaleX(${Math.min(1, surge / 8)})`;
    this.surgeValue.value = surge.toFixed(1);
    this.surgeMeter.parentElement?.setAttribute("aria-valuenow", surge.toFixed(1));
    document.body.dataset.overdrive = surge > 0 ? "true" : "false";
    const boss = snapshot.enemies.find((enemy) => enemy.type === "leviathan");
    this.bossRail.hidden = !boss || snapshot.phase !== "playing";
    if (boss) {
      const bossPercent = Math.max(0, (boss.hull / boss.maxHull) * 100);
      this.bossMeter.style.transform = `scaleX(${bossPercent / 100})`;
      this.bossValue.value = `${Math.ceil(bossPercent)}%`;
      this.bossMeter.parentElement?.setAttribute(
        "aria-valuenow",
        String(Math.ceil(bossPercent)),
      );
    }

    const cooldown = snapshot.player.sonarCooldown;
    this.sonarButton.disabled = cooldown > 0 || snapshot.phase !== "playing";
    byId<HTMLElement>("sonar-button-label").textContent =
      cooldown > 0 ? `${text.sonarCharging} ${cooldown.toFixed(1)}` : text.pulse;

    for (const button of this.systemButtons) {
      const system = button.dataset.system as SystemId;
      const power = snapshot.player.power[system];
      button.dataset.active = power >= 3 ? "true" : "false";
      button.disabled = snapshot.phase !== "playing";
      const pips = button.querySelector<HTMLElement>(".power-pips");
      if (pips && pips.childElementCount !== 4) {
        pips.replaceChildren(...Array.from({ length: 4 }, () => document.createElement("i")));
      }
      pips?.querySelectorAll("i").forEach((pip, index) => {
        pip.classList.toggle("on", index < power);
      });
    }

    if (snapshot.phase === "won" || snapshot.phase === "lost") {
      const won = snapshot.phase === "won";
      byId<HTMLElement>("result-title").textContent = won ? text.wonTitle : text.lostTitle;
      byId<HTMLElement>("result-summary").textContent = won ? text.wonSummary : text.lostSummary;
      byId<HTMLElement>("result-score").textContent = snapshot.score.toLocaleString();
      byId<HTMLElement>("result-hull").textContent = `${Math.ceil(hull)}%`;
      byId<HTMLElement>("result-kills").textContent = String(snapshot.kills);
      byId<HTMLElement>("result-combo").textContent = String(snapshot.player.highestCombo);
    }
  }

  updateAudio(enabled: boolean): void {
    const text = translation(this.language);
    byId<HTMLElement>("audio-button-label").textContent = enabled ? text.audioOn : text.audioOff;
  }

  currentLanguage(): Language {
    return this.language;
  }

  private applyLanguage(): void {
    const text = translation(this.language);
    document.documentElement.lang = this.language;
    byId<HTMLElement>("game-tagline").textContent = text.tagline;
    byId<HTMLElement>("start-button-label").textContent = text.begin;
    byId<HTMLElement>("hull-name").textContent = text.hull;
    byId<HTMLElement>("shield-name").textContent = text.shield;
    byId<HTMLElement>("power-name").textContent = text.power;
    byId<HTMLElement>("mission-kicker").textContent = text.relayOnline;
    byId<HTMLElement>("pause-title").textContent = text.paused;
    byId<HTMLElement>("resume-label").textContent = text.resume;
    byId<HTMLElement>("restart-label").textContent = text.diveAgain;
    byId<HTMLElement>("share-label").textContent = text.share;
    byId<HTMLElement>("result-score-name").textContent = text.score;
    byId<HTMLElement>("result-hull-name").textContent = text.hull;
    byId<HTMLElement>("result-kills-name").textContent = text.kills;
    byId<HTMLElement>("result-combo-name").textContent = text.bestChain;
    byId<HTMLElement>("boss-name").textContent = text.leviathan;
    byId<HTMLElement>("combo-name").textContent = text.chain;
    byId<HTMLElement>("surge-name").textContent = text.surge;
    this.sonarButton.querySelector("span")!.textContent = text.pulse;
    byId<HTMLElement>("power-total").textContent = `${TOTAL_POWER} / ${TOTAL_POWER}`;
    byId<HTMLElement>("timer-label").textContent = formatTime(RUN_DURATION);

    for (const button of document.querySelectorAll<HTMLButtonElement>("[data-language]")) {
      button.setAttribute("aria-pressed", String(button.dataset.language === this.language));
    }
    for (const button of this.systemButtons) {
      const system = button.dataset.system as SystemId;
      const label = systemLabel(this.language, system);
      button.querySelector<HTMLElement>(".system-name")!.textContent = label;
      button.setAttribute("aria-label", `${text.power}: ${label}`);
    }
    this.updateAudio(byId<HTMLElement>("audio-button-label").textContent !== text.audioOff);
  }

  private async share(): Promise<void> {
    const title = "Abyss Relay";
    const message =
      this.language === "ko"
        ? `Abyss Relay 잠수 기록: ${byId<HTMLElement>("result-score").textContent}점`
        : `My Abyss Relay run: ${byId<HTMLElement>("result-score").textContent} points`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: message, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(`${message} ${window.location.href}`);
        this.showToast(translation(this.language).copied);
      }
    } catch (error) {
      if ((error as DOMException).name !== "AbortError") {
        await navigator.clipboard.writeText(`${message} ${window.location.href}`);
        this.showToast(translation(this.language).copied);
      }
    }
  }

  private showToast(message: string): void {
    window.clearTimeout(this.toastTimer);
    this.toast.textContent = message;
    this.toast.hidden = false;
    this.toastTimer = window.setTimeout(() => {
      this.toast.hidden = true;
    }, 2_000);
  }
}
